import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { Callout, Dialog, Loading, toast } from '../ui.js';

/**
 * Cancel, resume and remove for good (owner 2026-11-02, thread "Ta bort videor, granskningar och projekt"). All of it
 * sits under Fler val. Cancelling is the normal way (history and files stay, Resume brings it back); removing for good
 * needs an extra confirmation: the project's name typed, or a tick for a video. Monday's projects and videos are
 * cancelled in Monday, which the Hub follows.
 */

const TX = texts(
  {
    cancelMenu: 'Cancel the project…',
    removeMenu: 'Remove the project for good…',
    cancelTitle: (name: string) => `Cancel ${name}?`,
    cancelBody: 'The project leaves the project list, My work and time. Open reviews are withdrawn, so nobody is reminded. Notes, files and history stay, and you can resume it.',
    reason: 'Why? (optional)',
    reasonHint: 'Shown on the project and in its activity.',
    cancelShoots: 'Cancel the booked shoots too (the calendar invites are cancelled)',
    cancel: 'Cancel the project',
    keep: 'Keep it',
    cancelled: 'The project is cancelled',
    cancelledShoots: (n: number) => (n === 1 ? ' One shoot was cancelled.' : n ? ` ${n} shoots were cancelled.` : ''),
    bannerTitle: 'This project is cancelled',
    bannerReason: (r: string) => `Reason: ${r}`,
    resume: 'Resume',
    resumed: 'The project is back',
    removeTitle: (name: string) => `Remove ${name} for good?`,
    removeLead: 'Tick what goes with it. It cannot be undone.',
    removeBody: (videos: number, reviews: number) =>
      `The project, its ${videos === 1 ? 'video' : `${videos} videos`}${reviews ? `, ${reviews === 1 ? 'one review' : `${reviews} reviews`}` : ''}, shoots, notes and brief`,
    removeShoots: (n: number) => (n === 1 ? 'Cancel the booked shoot and its calendar invite' : `Cancel the ${n} booked shoots and their calendar invites`),
    removeFolders: (name: string | null) => `Move the Drive folders${name ? ` “${name}”` : ''} to the trash`,
    removeFoldersHint: 'They can be restored from the Drive trash for 30 days. Freelancers lose access to the folder either way.',
    removeOtherFolders: 'The project’s Drive folders were not made by the Hub and stay.',
    removedFolders: (n: number): string => (n ? ' The Drive folders are in the trash.' : ''),
    removeMonday: 'The project’s item on Monday stays: remove it there yourself.',
    typeName: (name: string) => `Type “${name}” to confirm`,
    remove: 'Remove for good',
    removed: (name: string) => `${name} is removed`,
    close: 'Close',
    removeVideoMenu: 'Remove for good…',
    removeVideoTitle: (name: string) => `Remove ${name} for good?`,
    removeVideoBody: 'The video, its reviews, history and notifications are removed. It cannot be undone. Time logged on it stays on the project. To keep the history, choose Drop video… instead.',
    trashFiles: (n: number) => (n === 1 ? 'Move the file sent for review to the Drive trash' : `Move the ${n} files sent for review to the Drive trash`),
    trashFilesHint: 'They can be restored from the Drive trash for 30 days. Untick to keep them in Drive.',
    understand: 'I understand that this cannot be undone',
    removedVideo: (name: string) => `${name} is removed`
  },
  {
    cancelMenu: 'Avbryt projektet…',
    removeMenu: 'Ta bort projektet för gott…',
    cancelTitle: (name: string) => `Avbryta ${name}?`,
    cancelBody: 'Projektet försvinner från projektlistan, Mitt arbete och tid. Öppna granskningar dras tillbaka, så ingen får påminnelser. Anteckningar, filer och historik finns kvar, och du kan återuppta det.',
    reason: 'Varför? (valfritt)',
    reasonHint: 'Syns på projektet och i dess aktivitet.',
    cancelShoots: 'Ställ in de bokade inspelningarna också (kalenderinbjudan avbokas)',
    cancel: 'Avbryt projektet',
    keep: 'Behåll',
    cancelled: 'Projektet är avbrutet',
    cancelledShoots: (n: number) => (n === 1 ? ' En inspelning ställdes in.' : n ? ` ${n} inspelningar ställdes in.` : ''),
    bannerTitle: 'Projektet är avbrutet',
    bannerReason: (r: string) => `Anledning: ${r}`,
    resume: 'Återuppta',
    resumed: 'Projektet är tillbaka',
    removeTitle: (name: string) => `Ta bort ${name} för gott?`,
    removeLead: 'Bocka i det som ska följa med. Det går inte att ångra.',
    removeBody: (videos: number, reviews: number) =>
      `Projektet, ${videos === 1 ? 'videon' : `${videos} videor`}${reviews ? `, ${reviews === 1 ? 'en granskning' : `${reviews} granskningar`}` : ''}, inspelningar, anteckningar och brief`,
    removeShoots: (n: number) => (n === 1 ? 'Ställ in den bokade inspelningen och avboka kalenderinbjudan' : `Ställ in de ${n} bokade inspelningarna och avboka kalenderinbjudningarna`),
    removeFolders: (name: string | null) => `Flytta Drive-mapparna${name ? ` ”${name}”` : ''} till papperskorgen`,
    removeFoldersHint: 'De går att återställa från papperskorgen i Drive i 30 dagar. Frilansare förlorar åtkomsten till mappen oavsett.',
    removeOtherFolders: 'Projektets Drive-mappar gjordes inte av Hubben och finns kvar.',
    removedFolders: (n: number): string => (n ? ' Drive-mapparna ligger i papperskorgen.' : ''),
    removeMonday: 'Projektets rad i Monday finns kvar: ta bort den där själv.',
    typeName: (name: string) => `Skriv ”${name}” för att bekräfta`,
    remove: 'Ta bort för gott',
    removed: (name: string) => `${name} är borttaget`,
    close: 'Stäng',
    removeVideoMenu: 'Ta bort för gott…',
    removeVideoTitle: (name: string) => `Ta bort ${name} för gott?`,
    removeVideoBody: 'Videon, dess granskningar, historik och aviseringar tas bort. Det går inte att ångra. Tid som loggats på den finns kvar på projektet. Vill du behålla historiken väljer du Stryk videon… i stället.',
    trashFiles: (n: number) => (n === 1 ? 'Flytta filen som skickades för granskning till papperskorgen i Drive' : `Flytta de ${n} filerna som skickades för granskning till papperskorgen i Drive`),
    trashFilesHint: 'De går att återställa från papperskorgen i Drive i 30 dagar. Bocka ur för att spara dem i Drive.',
    understand: 'Jag förstår att det inte går att ångra',
    removedVideo: (name: string) => `${name} är borttagen`
  }
);

export const removeTexts = TX;

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

interface Changed<P> {
  project: P;
  text: string;
  undoId?: string | null;
}

/** Cancel: an optional reason, and whether the booked shoots go too. */
export function CancelProjectDialog<P>(p: { api: Api; id: string; name: string; hasShoots: boolean; onClose: () => void; onDone: (r: Changed<P>) => void }) {
  const t = TX[useLang()];
  const [reason, setReason] = useState('');
  const [shoots, setShoots] = useState(p.hasShoots);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await p.api.post<Changed<P> & { cancelledShoots: number }>(`/api/projects/${p.id}/cancel`, { reason: reason.trim(), cancelShoots: shoots });
      p.onDone({ ...r, text: t.cancelled + '.' + t.cancelledShoots(r.cancelledShoots) });
      p.onClose();
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.cancelTitle(p.name)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.keep}
          </button>
          <button type="button" class="btn btn-danger" disabled={busy} onClick={() => void run()}>
            {t.cancel}
          </button>
        </>
      }
    >
      <div class="stack">
        <p>{t.cancelBody}</p>
        <div class="field">
          <label class="label" for="cp-reason">
            {t.reason}
          </label>
          <textarea id="cp-reason" class="textarea" rows={2} maxLength={300} value={reason} onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)} aria-describedby="cp-reason-hint" />
          <span class="hint" id="cp-reason-hint">
            {t.reasonHint}
          </span>
        </div>
        {p.hasShoots ? (
          <label class="check">
            <input type="checkbox" checked={shoots} onChange={(e) => setShoots((e.currentTarget as HTMLInputElement).checked)} /> {t.cancelShoots}
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}

interface RemoveInfo {
  name: string;
  blocker: string | null;
  videos: number;
  reviews: number;
  upcomingShoots: number;
  onMonday: boolean;
  /** the Drive folders the Hub made (null: none, or Drive is not connected) */
  folders?: { name: string | null } | null;
  otherFolders?: boolean;
}

/** Remove for good: what goes with it, why it cannot (logged time, invoiced), and the project's name typed to confirm. */
export function RemoveProjectDialog(p: { api: Api; id: string; onClose: () => void; onRemoved: () => void }) {
  const t = TX[useLang()];
  const [info, setInfo] = useState<RemoveInfo | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  // everything ticked from the start; untick what should stay (owner 2026-11-04)
  const [shoots, setShoots] = useState(true);
  const [folders, setFolders] = useState(true);
  useEffect(() => {
    p.api.get<RemoveInfo>(`/api/projects/${p.id}/remove`).then(setInfo, (e) => (toast({ text: errText(e) }), p.onClose()));
  }, [p.id]);
  const ok = Boolean(info && !info.blocker && typed.trim() === info.name);
  const run = async () => {
    if (!info) return;
    setBusy(true);
    try {
      const r = await p.api.post<{ trashedFolders?: number }>(`/api/projects/${p.id}/remove`, { confirm: typed.trim(), cancelShoots: shoots, trashFolders: Boolean(info.folders) && folders });
      toast({ text: t.removed(info.name) + t.removedFolders(r?.trashedFolders ?? 0) });
      p.onRemoved();
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={info ? t.removeTitle(info.name) : t.removeMenu.replace('…', '')}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {info?.blocker ? t.close : t.keep}
          </button>
          {info && !info.blocker ? (
            <button type="button" class="btn btn-danger-solid" disabled={!ok || busy} onClick={() => void run()}>
              {t.remove}
            </button>
          ) : null}
        </>
      }
    >
      {!info ? (
        <Loading />
      ) : info.blocker ? (
        <Callout tone="warn" title={info.blocker} />
      ) : (
        <div class="stack">
          <p>{t.removeLead}</p>
          <label class="check">
            <input type="checkbox" checked disabled /> {t.removeBody(info.videos, info.reviews)}
          </label>
          {info.upcomingShoots ? (
            <label class="check">
              <input type="checkbox" checked={shoots} onChange={(e) => setShoots((e.currentTarget as HTMLInputElement).checked)} /> {t.removeShoots(info.upcomingShoots)}
            </label>
          ) : null}
          {info.folders ? (
            <div>
              <label class="check">
                <input type="checkbox" checked={folders} onChange={(e) => setFolders((e.currentTarget as HTMLInputElement).checked)} /> {t.removeFolders(info.folders.name)}
              </label>
              <p class="small muted">{t.removeFoldersHint}</p>
            </div>
          ) : info.otherFolders ? (
            <p class="small muted">{t.removeOtherFolders}</p>
          ) : null}
          {info.onMonday ? <p>{t.removeMonday}</p> : null}
          <div class="field">
            <label class="label" for="rp-name">
              {t.typeName(info.name)}
            </label>
            <input id="rp-name" class="input input--wide" autocomplete="off" value={typed} onInput={(e) => setTyped((e.target as HTMLInputElement).value)} />
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** Remove a video for good: a tick that it cannot be undone, then the button. */
export function RemoveVideoDialog<P>(p: { api: Api; projectId: string; video: { id: string; name: string }; onClose: () => void; onDone: (r: Changed<P>) => void }) {
  const t = TX[useLang()];
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const files = useFilesInfo(p.api, `/api/projects/${p.projectId}/videos/${p.video.id}/remove`);
  const [trash, setTrash] = useState(true);
  const run = async () => {
    setBusy(true);
    try {
      const r = await p.api.post<Changed<P> & { filesError?: string | null }>(`/api/projects/${p.projectId}/videos/${p.video.id}/remove`, { confirm: true, trashFiles: Boolean(files?.canTrash && files.files) && trash });
      if (r.filesError) toast({ text: r.filesError });
      p.onDone({ ...r, text: t.removedVideo(p.video.name), undoId: null });
      p.onClose();
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.removeVideoTitle(p.video.name)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.keep}
          </button>
          <button type="button" class="btn btn-danger-solid" disabled={!sure || busy} onClick={() => void run()}>
            {t.remove}
          </button>
        </>
      }
    >
      <div class="stack">
        <p>{t.removeVideoBody}</p>
        <TrashFiles info={files} on={trash} set={setTrash} />
        <label class="check">
          <input type="checkbox" checked={sure} onChange={(e) => setSure((e.currentTarget as HTMLInputElement).checked)} /> {t.understand}
        </label>
      </div>
    </Dialog>
  );
}

/** The cancelled project's banner on every tab: why, and Resume or Remove for good for the producer. */
export function CancelledBanner<P>(p: { api: Api; id: string; reason: string | null; canAct: boolean; onResumed: (r: Changed<P>) => void; onRemove: () => void }) {
  const t = TX[useLang()];
  const resume = async () => {
    try {
      const r = await p.api.post<Changed<P>>(`/api/projects/${p.id}/resume`);
      p.onResumed({ ...r, text: t.resumed });
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  return (
    <Callout tone="warn" title={t.bannerTitle}>
      {p.reason ? <p>{t.bannerReason(p.reason)}</p> : null}
      {p.canAct ? (
        <p class="cluster">
          <button type="button" class="btn btn-sm" onClick={() => void resume()}>
            {t.resume}
          </button>
          <button type="button" class="btn btn-sm btn-danger" onClick={p.onRemove}>
            {t.removeMenu}
          </button>
        </p>
      ) : null}
    </Callout>
  );
}

export interface FilesInfo {
  files: number;
  canTrash: boolean;
}

/** How many files sent for review go with a video or version, and whether the Hub can move them to the Drive trash. */
export function useFilesInfo(api: Api, path: string): FilesInfo | null {
  const [info, setInfo] = useState<FilesInfo | null>(null);
  useEffect(() => {
    api.get<FilesInfo>(path).then(setInfo, () => setInfo(null));
  }, [path]);
  return info;
}

/** The tick for the review files (owner 2026-11-04: ticked from the start, untick to keep them in Drive). */
export function TrashFiles(p: { info: FilesInfo | null; on: boolean; set: (on: boolean) => void }) {
  const t = TX[useLang()];
  if (!p.info?.canTrash || !p.info.files) return null;
  return (
    <div>
      <label class="check">
        <input type="checkbox" checked={p.on} onChange={(e) => p.set((e.currentTarget as HTMLInputElement).checked)} /> {t.trashFiles(p.info.files)}
      </label>
      <p class="small muted">{t.trashFilesHint}</p>
    </div>
  );
}
